import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";
import axios from "axios";
import { Dropdown, Form } from "capitalrx-components-2.0/lib/forms";
import { Button, LoadingGroup, LoadingSkeleton, Tag } from "capitalrx-components-2.0/lib/general";
import { Grid, Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Body, Paragraph, Semibold } from "capitalrx-components-2.0/lib/typography";
import { calculatePageCount, isValidLatitude, isValidLongitude } from "capitalrx-utilities";
import { priceDrugAPI } from "@/api/axios";
import { getFormularyAPI } from "@/api/axios/formulary";
import { getPharmaciesAPI, getPharmacyAPI } from "@/api/axios/pharmacies";
import { DrugPrice, DrugPricingResponse, ListPharmacyPayload, ListPharmacyReturn, PharmacyInfo, SubmissionClarificationCode } from "@/api/types";
import { AppleMap, MapBounds, MapLocation, PharmacyMapCallout } from "@/basic/components";
import { Pagination, TextDropdown, Toggle } from "@/common/components";
import { SortSvg } from "@/common/components/Icons";
import { ThemedP } from "@/common/components/themed/typography";
import { DEFAULT_MAP_COORDINATES } from "@/common/constants/mapConstants";
import { useExternalLink, useNavigateSearch, useSelectedEnrollmentPlan } from "@/premium/hooks";
import { CombinedPharmacyAndDrugInfo, PharmacyCard } from "@/premium/pages/Pharmacies/PharmacyCard";
import { Card, ConditionalErrorModal, NeedHelp, PageLayout } from "@/premium/themedComponents";
import { Breadcrumbs } from "@/premium/themedComponents/Breadcrumbs";
import { useEnrollmentContext } from "@/state";
import { ANALYTIC_EVENTS, logEvent } from "@/utils/analytics";

import { orderPharmaciesForCards } from "./pharmacyOrder";
import { SearchDetailsSection } from "./SearchDetailsSection";

import "./styles.scss";

const DRUG_TYPES = {
  BRAND: "Brand",
  GENERIC: "Generic",
  BOTH: "Both",
};

const NOT_ON_FORMULARY = "MR";
const PRICE_MAY_VARY = "UU";
const EXCLUDED = "70";
const PA = "75";
const QUANTITY_LIMIT = "9G";
const SPECIAL_APPROVAL = [PA, "608"];
const SUPPRESSIBLE = [...SPECIAL_APPROVAL, PRICE_MAY_VARY, QUANTITY_LIMIT];

const REJECT_TYPES = {
  NOT_ON_FORMULARY,
  PRICE_MAY_VARY,
  EXCLUDED,
  SPECIAL_APPROVAL,
  SUPPRESSIBLE,
};

const RESULTS_PER_PAGE = 6;

type SortValues = "distance" | "low_to_high";

const SORT_OPTIONS = [
  {
    value: "distance",
    label: "Distance",
  },
  {
    value: "low_to_high",
    label: "Price: Low to High",
  },
];

export const Pharmacies: FC = () => {
  const { i18n, t: translate } = useTranslation();

  const openExternalLink = useExternalLink();

  const { halfColumns } = useResponsive({
    halfColumns: { desktop: 6, tablet: 6, mobile: 12 },
  });

  const navigate = useNavigateSearch();
  const [searchParams, setSearchParams] = useSearchParams();
  const enrollmentPlanId = searchParams.get("enrollmentPlan");

  const [pharmacyData, setPharmacyData] = useState<ListPharmacyReturn["results"]>(undefined);
  const [mailPharmacy, setMailPharmacy] = useState<CombinedPharmacyAndDrugInfo>();
  const [isLoadingMailPharmacy, setIsLoadingMailPharmacy] = useState(false);
  const [onlinePharmacy, setOnlinePharmacy] = useState<CombinedPharmacyAndDrugInfo>();
  const [isLoadingOnlinePharmacy, setIsLoadingOnlinePharmacy] = useState(false);
  const [pharmacyResults, setPharmacyResults] = useState<ListPharmacyReturn["results"]>(undefined);
  const [isLoadingPharmacies, setIsLoadingPharmacies] = useState(true);
  const [drugPricing, setDrugPricing] = useState<DrugPricingResponse>(undefined);
  const [isLoadingDrugPricing, setIsLoadingDrugPricing] = useState<boolean>(false);
  const [isNoDeductible, setIsNoDeductible] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [latitude, setLatitude] = useState(Number(searchParams.get("latitude")) || DEFAULT_MAP_COORDINATES.lat);
  const [longitude, setLongitude] = useState(Number(searchParams.get("longitude")) || DEFAULT_MAP_COORDINATES.lng);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalText, setModalText] = useState(<></>);
  const [modalTitle, setModalTitle] = useState("Alert");
  const [canContinue, setCanContinue] = useState<boolean>(false);
  const [modalHasShown, setModalHasShown] = useState<boolean>(searchParams.get("modal_has_shown") === "true");
  const [isACAHIVPrEPRejection, setIsACAHIVPrEPRejection] = useState<[boolean, string | null]>([false, null]);
  const [isCopayMaximizerProgram, setIsCopayMaximizerProgram] = useState<boolean>(false);
  const [sortBy, setSortBy] = useState<SortValues>("distance");
  const [formularyUrl, setFormularyUrl] = useState<string>();

  const submissionClarificationCodeForm = useForm<SubmissionClarificationCode>();

  const { isMobile } = useResponsive({
    isMobile: { desktop: false, tablet: false, mobile: true },
  });

  const concatenatedPharmaciesList = useMemo(() => orderPharmaciesForCards(pharmacyResults), [pharmacyResults, sortBy]);

  const goBackToSearchPage = () => navigate("/plans/plan-details", { enrollmentPlan: enrollmentPlanId });

  const closeModal = useCallback(
    (approved: boolean) => {
      if (approved) {
        if (isCopayMaximizerProgram) {
          // For Copay Maximizers, just mark modal as shown and allow user to continue
          setModalHasShown(true);
          setSearchParams((prev) => {
            const upcoming = new URLSearchParams(prev);
            upcoming.set("modal_has_shown", "true");
            upcoming.set(
              "codes_to_suppress",
              JSON.stringify({
                suppress_prior_authorization: true,
                daw_code: "1",
                suppress_step_therapy: true,
              }),
            );
            return upcoming;
          });
          setModalVisible(false);
        } else if (isACAHIVPrEPRejection[0]) {
          const submission_clarification_code = submissionClarificationCodeForm.getValues().submission_clarification_code;
          if (!submission_clarification_code) {
            submissionClarificationCodeForm.setError("submission_clarification_code", {
              message: translate("PHARMACIES.pharmacies.pleaseSelectAnOption"),
            });
            return;
          }
          setSearchParams((prev) => {
            const upcoming = new URLSearchParams(prev);
            if (submission_clarification_code == null) {
              upcoming.delete("submission_clarification_code");
            } else {
              upcoming.set(
                "submission_clarification_code",
                JSON.stringify({
                  program: isACAHIVPrEPRejection[1],
                  submission_clarification_code,
                }),
              );
            }
            return upcoming;
          });
        } else {
          setSearchParams((prev) => {
            const upcoming = new URLSearchParams(prev);
            upcoming.set(
              "codes_to_suppress",
              JSON.stringify({
                suppress_prior_authorization: true,
                daw_code: "1",
                suppress_step_therapy: true,
              }),
            );
            upcoming.set("modal_has_shown", "true");
            return upcoming;
          });
        }
        setModalHasShown(true);
        setModalVisible(false);
      } else {
        goBackToSearchPage();
      }
    },
    [enrollmentPlanId, navigate, isACAHIVPrEPRejection, isCopayMaximizerProgram, submissionClarificationCodeForm, setSearchParams],
  );

  const experienceData = useEnrollmentContext();

  const whitelabelClient = experienceData.experience.identifier;

  const daysSupply = useMemo(() => {
    const param = searchParams.get("daysSupply");
    if (param) {
      return decodeURIComponent(param);
    }
    return undefined;
  }, [searchParams]);

  const dosage = useMemo(() => {
    const param = searchParams.get("dosage");
    if (param) {
      return decodeURIComponent(param);
    }
    return undefined;
  }, [searchParams]);

  const quantity = useMemo(() => {
    const param = searchParams.get("quantity");
    if (param) {
      return decodeURIComponent(param);
    }
    return undefined;
  }, [searchParams]);

  const dosageForm = useMemo(() => {
    const param = searchParams.get("dosageForm");
    if (param) {
      return decodeURIComponent(param);
    }
    return undefined;
  }, [searchParams]);

  const drugId = useMemo(() => {
    const param = searchParams.get("drugId");
    if (param) {
      return +param;
    }
    return undefined;
  }, [searchParams]);

  const hasDrugInformation = dosage && quantity && dosageForm && drugId;

  const showPharmaciesPrint = useMemo(() => {
    return !dosage && !quantity && !dosageForm && !drugId;
  }, [dosage, quantity, dosageForm, drugId]);

  const age = searchParams.get("age");
  const fullGender = searchParams.get("gender");

  const gender = useMemo(() => {
    if (fullGender === "Male") {
      return "M";
    } else if (fullGender === "Other") {
      return "U";
    } else if (fullGender === "X") {
      return "X";
    }
    return "F";
  }, [fullGender]);

  const claimRunDate = useMemo(() => {
    const param = searchParams.get("claimRunDate");
    if (param) {
      const decoded = decodeURIComponent(param);
      return new Date(decoded).toISOString();
    }
    return undefined;
  }, [searchParams]);

  const submissionClarificationCode = useMemo(() => {
    try {
      return JSON.parse(searchParams.get("submission_clarification_code"));
    } catch (err) {
      console.error(err);
    }
    return null;
  }, [searchParams]);

  const codesToSuppress = useMemo(() => {
    try {
      return JSON.parse(searchParams.get("codes_to_suppress"));
    } catch (err) {
      console.error(err);
    }
  }, [searchParams]);

  const {
    enrollmentPlanCsid,
    hideMailPharmacyOnFBPTool,
    hideOnlinePharmacyOnFBPTool,
    mailPharmacyIdentifier,
    onlinePharmacyIdentifier,
    overrideFormularyLookupURL,
  } = useSelectedEnrollmentPlan();

  useEffect(() => {
    if (enrollmentPlanCsid) {
      getFormularyAPI(enrollmentPlanCsid.id)
        .then((formulary) => {
          setFormularyUrl(formulary.lookup_tool_url);
        })
        .catch(() => {
          setFormularyUrl(undefined);
        });
    }
  }, [enrollmentPlanCsid]);

  const sendToFormulary = () => {
    logEvent(ANALYTIC_EVENTS.VIEW_FORMULARY);
    const url = overrideFormularyLookupURL ?? formularyUrl;
    if (url) {
      openExternalLink(url);
    }
  };

  /* Prefer pharmacyNameSearch over pharmacyName since pharmacyNameSearch is used when navigating
     back to the pharmacies page from the pharmacy details page, and we want to retain the search term in that case.
  */
  const pharmacyName = searchParams.get("pharmacyNameSearch") || searchParams.get("pharmacyName");

  const zipCode = searchParams.get("zipCode");
  const center = {
    latitude: DEFAULT_MAP_COORDINATES.lat,
    longitude: DEFAULT_MAP_COORDINATES.lng,
  };
  if (isValidLatitude(latitude) && isValidLongitude(longitude)) {
    center.latitude = latitude;
    center.longitude = longitude;
  }

  let locationString = translate("PHARMACIES.pharmacies.nearCurrentLocation");
  if (zipCode) {
    locationString = `${translate("PHARMACIES.pharmacies.in")} ${zipCode}`;
  }
  if (hasDrugInformation) {
    locationString = `${translate("PHARMACIES.pharmacies.for")} "${drugPricing?.drug.drug_name}" ${locationString}`;
  }
  if (pharmacyName && !hasDrugInformation) {
    locationString = `${translate("PHARMACIES.pharmacies.for")} "${pharmacyName}" ${locationString}`;
  }

  const priceOnePharmacy = useCallback(async(pharmacyId: string) => {
    return priceDrugAPI({
        enrollment_plan_id: enrollmentPlanCsid.id,
        pharmacy_npis: [pharmacyId],
        drug: {
          days_supply: Number(daysSupply),
          drug_id: drugId,
          dosage: dosage,
          dosage_form: dosageForm,
          quantity: +quantity,
        },
        claim: codesToSuppress,
        claim_run_date: claimRunDate,
        member: {
          age: +age,
          sex: gender ? gender : "F",
        },
        submission_clarification_code: submissionClarificationCode,
      });
    }, [
      gender,
      age,
      drugId,
      dosage,
      dosageForm,
      quantity,
      enrollmentPlanCsid,
      codesToSuppress,
      daysSupply,
      claimRunDate,
      submissionClarificationCode,
  ]);

  const fetchData = useCallback(async () => {
    setIsLoadingPharmacies(true);

    let payload: ListPharmacyPayload = {
      latitude: latitude.toString(),
      longitude: longitude.toString(),
      page_number: currentPage + 1,
      enrollment_plan_id: enrollmentPlanCsid.id,
      results_per_page: RESULTS_PER_PAGE,
      client_name: whitelabelClient,
    };

    if (pharmacyName) {
      payload = {
        ...payload,
        pharmacy_name: pharmacyName,
      };
    }

    if (drugId && dosageForm && dosage) {
      payload = {
        ...payload,
        drug_id: drugId,
        dosage_form: dosageForm,
        dosage: dosage,
      };
    }

    try {
      const pharmacies: ListPharmacyReturn = await getPharmaciesAPI(payload);
      setPageCount(calculatePageCount(parseInt(pharmacies["result_count"]), RESULTS_PER_PAGE));

      const SHOW_IF_NEARBY_MAX_DISTANCE_MILES = 25;
      const displayOnMapPharmacies = pharmacies["results"].filter((p) => {
        const rule = p.pharmacy.pharmacy_display_rules?.display_on_map;
        if (rule === "never") return false;
        if (rule === "check") {
          const dist =
            p.pharmacy.distance !== null && p.pharmacy.distance !== undefined
              ? Number(p.pharmacy.distance)
              : null;
          return dist === null || dist <= SHOW_IF_NEARBY_MAX_DISTANCE_MILES;
        }
        return true;
      });

      setPharmacyData(displayOnMapPharmacies);
      setPharmacyResults(pharmacies["results"]);
    } catch (err) {
      if (axios.isAxiosError(err)) {
        if (err?.response?.status === 404) {
          setCanContinue(false);
          if (pharmacyName) {
            setModalText(
              <div>
                <p>
                  <Trans
                    i18nKey="PHARMACIES.pharmacies.couldNotFindAnyPharmacies"
                    values={{
                      pharmacyName: pharmacyName,
                    }}
                  />
                </p>
              </div>,
            );
          } else {
            setModalText(
              <div>
                <p>{translate("PHARMACIES.pharmacies.couldNotFindPharmaciesInTheSearchArea")}</p>
              </div>,
            );
          }
          setModalVisible(true);
        }
      }
    }

    setIsLoadingPharmacies(false);
  }, [currentPage, latitude, longitude, pharmacyName, enrollmentPlanCsid, whitelabelClient, drugId, dosage, dosageForm]);

  useEffect(() => {
    if (enrollmentPlanCsid) {
      fetchData();
    }
  }, [enrollmentPlanCsid, fetchData]);

  useEffect(() => {
    const shouldLoadDrug = !!drugId && !!dosage && !!dosageForm && !!quantity;
    setIsLoadingDrugPricing(shouldLoadDrug);
    if (shouldLoadDrug && concatenatedPharmaciesList && concatenatedPharmaciesList.length > 0) {
      priceDrugAPI({
        enrollment_plan_id: enrollmentPlanCsid.id,
        pharmacy_npis: concatenatedPharmaciesList.map((pd) => pd.pharmacy.npi),
        drug: {
          days_supply: Number(daysSupply),
          drug_id: drugId,
          dosage: dosage,
          dosage_form: dosageForm,
          quantity: +quantity,
        },
        claim: codesToSuppress,
        claim_run_date: claimRunDate,
        member: {
          age: +age,
          sex: gender ? gender : "F",
        },
        submission_clarification_code: submissionClarificationCode,
      })
        .then(setDrugPricing)
        .finally(() => {
          setIsLoadingDrugPricing(false);
        });
    }
  }, [
    gender,
    age,
    drugId,
    dosage,
    dosageForm,
    quantity,
    concatenatedPharmaciesList,
    enrollmentPlanCsid,
    codesToSuppress,
    daysSupply,
    claimRunDate,
    submissionClarificationCode,
  ]);

  const setSubtract = (a: Set<string>, b: Set<string>) => new Set([...a].filter((x) => !b.has(x)));

  function setIntersection(set1: Set<string>, set2: Set<string>): Set<string> {
    const ans = new Set<string>();
    for (const i of set2) {
      if (set1.has(i)) {
        ans.add(i);
      }
    }
    return ans;
  }

  const sussOutRejectCodes = useCallback((priceList: DrugPrice[]) => {
    const codes = priceList.reduce(
      (accum: { brand: string[]; generic: string[] }, drugPrice) => {
        if (drugPrice.drug_type === DRUG_TYPES.BRAND) {
          const brandRejectCodes = [...accum.brand, ...drugPrice.reject_codes];
          return { ...accum, brand: brandRejectCodes };
        } else if (drugPrice.drug_type === DRUG_TYPES.GENERIC) {
          const genericRejectCodes = [...accum.generic, ...drugPrice.reject_codes];
          return { ...accum, generic: genericRejectCodes };
        } else {
          return accum;
        }
      },
      { generic: [], brand: [] },
    );

    const genericSet = new Set<string>(codes.generic);
    const brandSet = new Set<string>(codes.brand);

    const commonSet = setIntersection(genericSet, brandSet);

    const genericWithoutCommonSet = setSubtract(genericSet, commonSet);
    const brandWithoutCommonSet = setSubtract(brandSet, commonSet);

    const ret = {
      generic: [...genericWithoutCommonSet],
      brand: [...brandWithoutCommonSet],
      common: [...commonSet],
    };

    return ret;
  }, []);

  const getAllPrograms = useCallback((priceList: DrugPrice[]): string[] => {
    const allPrograms = new Set<string>();
    priceList.forEach((p) => {
      for (const program of p.programs || []) {
        allPrograms.add(program);
      }
    });
    return [...allPrograms];
  }, []);

  const isACAHIVPrEPRejectionCheck = useCallback((rejectCodes: string[], programs: string[]): [boolean, null | string] => {
    if (rejectCodes.length == 1 && rejectCodes[0] == "34") {
      for (const program of programs) {
        if (
          program == "capital rx/formulary/affordable care act/ACA HIV PrEP"
          || program == "capital rx/formulary/affordable care act/ACA HIV PrEP PA"
        ) {
          return [true, program];
        }
      }
    }
    return [false, null];
  }, []);

  const isCopayMaximizerProgramCheck = useCallback((programs: string[]): boolean => {
    for (const program of programs) {
      const lowerProgram = program.toLowerCase();
      if (lowerProgram.startsWith("capital rx/rx cap") || lowerProgram.startsWith("capital rx/copay maximizer")) {
        return true;
      }
    }
    return false;
  }, []);

  const containsNonSuppressibleRejectCodes = (rejectCodes: string[]) => {
    const suppressibleCodes = ["UU", PA, "608"];
    const diff = rejectCodes.filter((x) => !suppressibleCodes.includes(x));
    return !!diff.length;
  };

  const containsGeneric = (pricing: DrugPrice[]) => {
    const generics = pricing.filter((drugPrice) => drugPrice.drug_type === DRUG_TYPES.GENERIC);
    return generics.length > 0;
  };

  const generateModalMessage = useCallback(
    (pricing: DrugPricingResponse, rejectCodes: { brand: string[]; generic: string[]; common: string[] }) => {
      const modalTitles = {
        notOnFormulary: translate("PHARMACIES.pharmacies.thisDrugMayNotBeOnFormulary"),
        insuppressible: translate("PHARMACIES.pharmacies.pricingIsNotAvailableForThisDrug"),
        specialApproval: translate("PHARMACIES.pharmacies.thisDrugMayNeedToBeApproved"),
        priceMayVary: translate("PHARMACIES.pharmacies.pricingMayVary"),
        partialResults: translate("PHARMACIES.pharmacies.pricingIsNotAvailable"),
        quantityLimitExceeded: translate("PHARMACIES.pharmacies.quantityLimitExceeded"),
      };

      const hasAnyPricing = pricing.pricing.some((price) => {
        return price.reject_codes.length === 0 || price.reject_codes.every((reject) => REJECT_TYPES.SUPPRESSIBLE.includes(reject));
      });
      const hasAnyInsuppressible = pricing.pricing.some((price) => {
        return price.reject_codes.some((reject) => !REJECT_TYPES.SUPPRESSIBLE.includes(reject));
      });
      const hasPartialResults = hasAnyPricing && hasAnyInsuppressible;
      const anyRowHasOnlyPARejectCode = pricing.pricing.some(
        (price) => price.reject_codes?.length === 1 && price.reject_codes?.includes(PA),
      );
      const anyRowHasOnlyQLRejectCode = pricing.pricing.some(
        (price) => price.reject_codes?.length === 1 && price.reject_codes?.includes(QUANTITY_LIMIT),
      );
      const has_formulary_alternative = pricing.pricing.some((price) => {
        return price.has_formulary_alternative;
      });

      const rejectMessages = {
        notOnFormularyOrExcluded: (drug: string, type: string, has_formulary_alternative: boolean) => {
          if (has_formulary_alternative) {
            return (
              <p>
                <Trans
                  i18nKey={"PHARMACIES.pharmacies.theVersionMayNotBeCovered"}
                  values={{ type: type.toLowerCase(), drug: drug }}
                  components={{ b: <b /> }}
                />{" "}
              </p>
            );
          } else {
            return (
              <p>
                <Trans
                  i18nKey={"PHARMACIES.pharmacies.theVersionMayNotBeCoveredOnTheFormulary"}
                  values={{ type: type.toLowerCase(), drug: drug }}
                  components={{ b: <b /> }}
                />{" "}
              </p>
            );
          }
        },
        insuppressible: (drug: string, type: string) => (
          <p>
            <Trans
              i18nKey={"PHARMACIES.pharmacies.weAreUnableToPriceTheVersion"}
              values={{ type: type.toLowerCase(), drug: drug }}
              components={{ b: <b /> }}
            />{" "}
          </p>
        ),
        specialApproval: (drug: string, type: string) => (
          <p>
            <Trans
              i18nKey={"PHARMACIES.pharmacies.forTheVersionToBeCovered"}
              values={{ type: type.toLowerCase(), drug: drug }}
              components={{ b: <b /> }}
            />{" "}
          </p>
        ),
        priceMayVary: (drug: string, type: string) => (
          <p>
            <Trans
              i18nKey={"PHARMACIES.pharmacies.thePriceOfTheVersionCouldVary"}
              values={{ type: type.toLowerCase(), drug: drug }}
              components={{ b: <b /> }}
            />{" "}
          </p>
        ),
        partialResults: () => <p>{translate("PHARMACIES.pharmacies.thisDrugCouldNotBePriced")}</p>,
        partialResultsNoBrandSomeGenericInsuppressable: (brandName: string, genericName: string) => (
          <p>
            <Trans
              i18nKey={"PHARMACIES.pharmacies.weAreUnableToPrice"}
              values={{ brandName: brandName, genericName: genericName }}
              components={{ b: <b /> }}
            />
          </p>
        ),
        quantityLimitExceeded: (quantity: string) => (
          <p>
            <Trans i18nKey={"PHARMACIES.pharmacies.viewFormularyQuantityLimit"} values={{ quantity }} components={{ b: <b /> }} />
          </p>
        ),
        none: (drug: string, type: string) => (
          <p>
            <Trans
              i18nKey={"PHARMACIES.pharmacies.youCanStillViewPricing"}
              values={{ type: type.toLowerCase(), drug: drug }}
              components={{ b: <b /> }}
            />{" "}
          </p>
        ),
      };

      const commonRejectMessages = {
        notOnFormularyOrExcluded: (brandName: string, genericName: string, has_formulary_alternative: boolean) => {
          if (has_formulary_alternative) {
            return (
              <p>
                <Trans
                  i18nKey={"PHARMACIES.pharmacies.genericVersionMayNotBeCovered"}
                  values={{ brandName: brandName, genericName: genericName }}
                  components={{ b: <b /> }}
                />{" "}
              </p>
            );
          } else {
            return (
              <p>
                <Trans
                  i18nKey={"PHARMACIES.pharmacies.genericVersionMayNotBeCoveredOnTheFormulary"}
                  values={{ brandName: brandName, genericName: genericName }}
                  components={{ b: <b /> }}
                />{" "}
              </p>
            );
          }
        },
        insuppressible: (brandName: string, genericName: string) => (
          <p>
            <Trans
              i18nKey={"PHARMACIES.pharmacies.weAreUnableToPriceGenericVersion"}
              values={{ brandName: brandName, genericName: genericName }}
              components={{ b: <b /> }}
            />{" "}
          </p>
        ),
        specialApproval: (brandName: string, genericName: string) => (
          <p>
            <Trans
              i18nKey={"PHARMACIES.pharmacies.forGenericVersionToBeCovered"}
              values={{ brandName: brandName, genericName: genericName }}
              components={{ b: <b /> }}
            />{" "}
          </p>
        ),
        priceMayVary: (brandName: string, genericName: string) => (
          <p>
            <Trans
              i18nKey={"PHARMACIES.pharmacies.thePriceCouldVaryBasedOnDetails"}
              values={{ brandName: brandName, genericName: genericName }}
              components={{ b: <b /> }}
            />{" "}
          </p>
        ),
      };

      let brandName = "";
      let genericName = "";
      let drugTypesPriced = "";
      if (pricing.generic_alternative && containsGeneric(pricing.pricing)) {
        // both
        brandName = pricing.drug.drug_name;
        genericName = pricing.generic_alternative.drug_name;
        drugTypesPriced = DRUG_TYPES.BOTH;
      } else if (!pricing.generic_alternative || !containsGeneric(pricing.pricing)) {
        // only generic
        if (pricing.pricing[0].drug_type === DRUG_TYPES.GENERIC) {
          genericName = pricing.drug.drug_name;
          drugTypesPriced = DRUG_TYPES.GENERIC;
        } else {
          brandName = pricing.drug.drug_name;
          drugTypesPriced = DRUG_TYPES.BRAND;
        }
      }

      const { generic, brand, common } = rejectCodes;

      let messages: JSX.Element[] = [];

      if (anyRowHasOnlyPARejectCode) {
        if (generic.length === 0 && brand.length > 0) {
          messages.push(rejectMessages.specialApproval(brandName, DRUG_TYPES.BRAND));
          logEvent(ANALYTIC_EVENTS.DRUG_PRICING_MESSAGE.brand.reject_special_approval);
        } else {
          messages.push(rejectMessages.specialApproval(genericName, DRUG_TYPES.GENERIC));
          logEvent(ANALYTIC_EVENTS.DRUG_PRICING_MESSAGE.generic.reject_special_approval);
        }
        return { messages, canSuppress: anyRowHasOnlyPARejectCode, title: modalTitles.specialApproval };
      }
      if (anyRowHasOnlyQLRejectCode) {
        messages.push(rejectMessages.quantityLimitExceeded(quantity));
        if (formularyUrl) {
          messages.push(
            <Button icon="link-out" appearance="text" onClick={sendToFormulary}>
              {translate("PHARMACIES.pharmacies.viewFormulary")}
            </Button>,
          );
        }
        logEvent(ANALYTIC_EVENTS.DRUG_PRICING_MESSAGE.common.reject_quantity_limit);
        return { messages, canSuppress: anyRowHasOnlyQLRejectCode, title: modalTitles.quantityLimitExceeded };
      }

      if (hasPartialResults) {
        /**
         * In some cases, the generic prices out just fine but not the brand for whatever reason
         *
         * If that's the case, we will want to display messaging indicating that the member cannot get
         * the brand drug, but can get the generic
         *
         * If there were issues pricing both, then we will just show the general partial results messaging
         */
        if (generic.length === 0 && brand.length > 0 && containsNonSuppressibleRejectCodes(brand)) {
          messages.push(rejectMessages.partialResultsNoBrandSomeGenericInsuppressable(brandName, genericName));
          logEvent(ANALYTIC_EVENTS.DRUG_PRICING_MESSAGE.partial_results_no_brand_some_generic_insuppressible);
        } else {
          messages.push(rejectMessages.partialResults());
          logEvent(ANALYTIC_EVENTS.DRUG_PRICING_MESSAGE.partial_results);
        }
        return { messages, canSuppress: hasPartialResults, title: modalTitles.partialResults };
      }

      if (common.includes(REJECT_TYPES.NOT_ON_FORMULARY) || common.includes(REJECT_TYPES.EXCLUDED)) {
        messages.unshift(commonRejectMessages.notOnFormularyOrExcluded(brandName, genericName, has_formulary_alternative));
        logEvent(ANALYTIC_EVENTS.DRUG_PRICING_MESSAGE.common.reject_mr_or_70);
        return { messages, canSuppress: hasPartialResults, title: modalTitles.notOnFormulary };
      }

      if (containsNonSuppressibleRejectCodes(common) && !hasPartialResults) {
        messages.unshift(commonRejectMessages.insuppressible(brandName, genericName));
        logEvent(ANALYTIC_EVENTS.DRUG_PRICING_MESSAGE.common.reject_insuppressible);
        return { messages, canSuppress: hasPartialResults, title: modalTitles.insuppressible };
      }

      if (drugTypesPriced === DRUG_TYPES.GENERIC) {
        if (generic.includes(REJECT_TYPES.NOT_ON_FORMULARY) || generic.includes(REJECT_TYPES.EXCLUDED)) {
          messages.unshift(rejectMessages.notOnFormularyOrExcluded(genericName, DRUG_TYPES.GENERIC, has_formulary_alternative));
          logEvent(ANALYTIC_EVENTS.DRUG_PRICING_MESSAGE.generic.reject_mr_or_70_generic_only);
          return {
            messages,
            canSuppress: hasPartialResults,
            title: modalTitles.notOnFormulary,
          };
        }
        if (containsNonSuppressibleRejectCodes(generic) && !hasPartialResults) {
          messages.unshift(rejectMessages.insuppressible(genericName, DRUG_TYPES.GENERIC));
          logEvent(ANALYTIC_EVENTS.DRUG_PRICING_MESSAGE.generic.reject_insuppressible_generic_only);
          return {
            messages,
            canSuppress: hasPartialResults,
            title: modalTitles.insuppressible,
          };
        }
      } else if (drugTypesPriced === DRUG_TYPES.BRAND) {
        if (brand.includes(REJECT_TYPES.NOT_ON_FORMULARY) || brand.includes(REJECT_TYPES.EXCLUDED)) {
          messages.unshift(rejectMessages.notOnFormularyOrExcluded(brandName, DRUG_TYPES.BRAND, has_formulary_alternative));
          logEvent(ANALYTIC_EVENTS.DRUG_PRICING_MESSAGE.brand.reject_mr_or_70_brand_only);
          return { messages, canSuppress: hasPartialResults, title: modalTitles.notOnFormulary };
        }
        if (containsNonSuppressibleRejectCodes(brand) && !hasPartialResults) {
          messages.unshift(rejectMessages.insuppressible(brandName, DRUG_TYPES.BRAND));
          logEvent(ANALYTIC_EVENTS.DRUG_PRICING_MESSAGE.brand.reject_insuppressible_brand_only);
          return { messages, canSuppress: hasPartialResults, title: modalTitles.insuppressible };
        }
      }

      // Other codes can be accumulated and suppressed.
      let newModalTitle = "";
      // reset for ordering purposes.
      messages = [];
      if (common.includes(REJECT_TYPES.PRICE_MAY_VARY)) {
        messages.push(commonRejectMessages.priceMayVary(brandName, genericName));
        logEvent(ANALYTIC_EVENTS.DRUG_PRICING_MESSAGE.common.reject_price_may_vary);
        newModalTitle = modalTitles.priceMayVary;
      }
      if (common.some((code) => REJECT_TYPES.SPECIAL_APPROVAL.includes(code))) {
        messages.push(commonRejectMessages.specialApproval(brandName, genericName));
        logEvent(ANALYTIC_EVENTS.DRUG_PRICING_MESSAGE.common.reject_special_approval);
        newModalTitle = modalTitles.specialApproval;
      }

      // BRAND
      if (brand.includes(REJECT_TYPES.NOT_ON_FORMULARY) || brand.includes(REJECT_TYPES.EXCLUDED)) {
        messages.push(rejectMessages.notOnFormularyOrExcluded(brandName, DRUG_TYPES.BRAND, has_formulary_alternative));
        logEvent(ANALYTIC_EVENTS.DRUG_PRICING_MESSAGE.brand.reject_mr_or_70);
        newModalTitle = modalTitles.notOnFormulary;
      } else if (containsNonSuppressibleRejectCodes(brand) && !hasPartialResults) {
        messages.push(rejectMessages.insuppressible(brandName, DRUG_TYPES.BRAND));
        logEvent(ANALYTIC_EVENTS.DRUG_PRICING_MESSAGE.brand.reject_insuppressible);
        newModalTitle = modalTitles.insuppressible;
      } else {
        if (brand.includes(REJECT_TYPES.PRICE_MAY_VARY)) {
          messages.push(rejectMessages.priceMayVary(brandName, DRUG_TYPES.BRAND));
          logEvent(ANALYTIC_EVENTS.DRUG_PRICING_MESSAGE.brand.reject_price_may_vary);
          newModalTitle = modalTitles.priceMayVary;
        }
        if (brand.some((code) => REJECT_TYPES.SPECIAL_APPROVAL.includes(code))) {
          messages.push(rejectMessages.specialApproval(brandName, DRUG_TYPES.BRAND));
          logEvent(ANALYTIC_EVENTS.DRUG_PRICING_MESSAGE.brand.reject_special_approval);
          newModalTitle = modalTitles.specialApproval;
        }
      }

      if (generic.includes(REJECT_TYPES.NOT_ON_FORMULARY) || generic.includes(REJECT_TYPES.EXCLUDED)) {
        messages.push(rejectMessages.notOnFormularyOrExcluded(genericName, DRUG_TYPES.GENERIC, has_formulary_alternative));
        logEvent(ANALYTIC_EVENTS.DRUG_PRICING_MESSAGE.generic.reject_mr_or_70);
        newModalTitle = modalTitles.notOnFormulary;
      } else if (containsNonSuppressibleRejectCodes(generic) && !hasPartialResults) {
        messages.push(rejectMessages.insuppressible(genericName, DRUG_TYPES.GENERIC));
        logEvent(ANALYTIC_EVENTS.DRUG_PRICING_MESSAGE.generic.reject_insuppressible);
        newModalTitle = modalTitles.insuppressible;
      } else {
        if (generic.includes(REJECT_TYPES.PRICE_MAY_VARY)) {
          messages.push(rejectMessages.priceMayVary(genericName, DRUG_TYPES.GENERIC));
          logEvent(ANALYTIC_EVENTS.DRUG_PRICING_MESSAGE.generic.reject_price_may_vary);
          newModalTitle = modalTitles.priceMayVary;
        }
        if (generic.some((code) => REJECT_TYPES.SPECIAL_APPROVAL.includes(code))) {
          messages.push(rejectMessages.specialApproval(genericName, DRUG_TYPES.GENERIC));
          logEvent(ANALYTIC_EVENTS.DRUG_PRICING_MESSAGE.generic.reject_special_approval);
          newModalTitle = modalTitles.specialApproval;
        }
      }

      if (drugTypesPriced === DRUG_TYPES.BOTH) {
        if (brand.length && !generic.length && !common.length) {
          messages.push(rejectMessages.none(genericName, DRUG_TYPES.GENERIC));
          logEvent(ANALYTIC_EVENTS.DRUG_PRICING_MESSAGE.generic.reject_none);
        }
        if (generic.length && !brand.length && !common.length) {
          messages.push(rejectMessages.none(brandName, DRUG_TYPES.BRAND));
          logEvent(ANALYTIC_EVENTS.DRUG_PRICING_MESSAGE.brand.reject_none);
        }
      }

      if (hasPartialResults) {
        messages.push(rejectMessages.partialResults());
        logEvent(ANALYTIC_EVENTS.DRUG_PRICING_MESSAGE.partial_results_bottom_case);
      }

      return { messages, canSuppress: true, title: newModalTitle };
    },
    [formularyUrl, overrideFormularyLookupURL],
  );

  useEffect(() => {
    if (drugPricing?.pricing) {
      const rejectCodes = sussOutRejectCodes(drugPricing.pricing);
      const allRejectCodes = rejectCodes.generic.concat(rejectCodes.brand).concat(rejectCodes.common);
      const allPrograms = getAllPrograms(drugPricing.pricing);
      if (!modalHasShown) {
        const isACAHIVPrEPRejection = isACAHIVPrEPRejectionCheck(allRejectCodes, allPrograms);
        setIsACAHIVPrEPRejection(isACAHIVPrEPRejection);
        if (isACAHIVPrEPRejection[0]) {
          setCanContinue(true);
          setModalTitle(translate("PHARMACIES.pharmacies.provideAdditionalDrugInformation"));
          setModalVisible(true);
        } else {
          const hasAdditionalMessaging = !!drugPricing?.custom_member_messages?.length;
          const modalMessages = [];
          let newModalTitle = translate("PHARMACIES.pharmacies.attention");
          let canViewResults = true;

          // Check for Copay Maximizer program first
          const hasCopayMaximizerProgram = isCopayMaximizerProgramCheck(allPrograms);
          setIsCopayMaximizerProgram(hasCopayMaximizerProgram);
          if (hasCopayMaximizerProgram) {
            setModalTitle(translate("PHARMACIES.pharmacies.copayMaximizerProgramNotice"));
            modalMessages.push(
              <div>
                <Trans i18nKey="PHARMACIES.pharmacies.copayMaximizerProgramMessage" components={{ b: <b /> }} />
              </div>,
            );
          }

          if (allRejectCodes.length) {
            const { messages, canSuppress, title } = generateModalMessage(drugPricing, rejectCodes);
            newModalTitle = title;
            canViewResults = canSuppress;
            modalMessages.push(messages);
          }

          if (hasAdditionalMessaging) {
            for (const message of drugPricing.custom_member_messages) {
              modalMessages.push(<p>{message}</p>);
            }
          }

          if (modalMessages.length) {
            setModalText(<div>{...modalMessages}</div>);
            setModalTitle(newModalTitle);
            setCanContinue(canViewResults);
            setModalVisible(true);
          }
        }
      }
    }
  }, [drugPricing, modalHasShown, generateModalMessage, sussOutRejectCodes]);

  const handleMapRedoSearch = (bounds: MapBounds) => {
    setLatitude(bounds.center.latitude);
    setLongitude(bounds.center.longitude);
  };

  const handleSelectPage = (pageNum: number) => {
    setDrugPricing(undefined);
    setPharmacyData(undefined);
    setIsLoadingDrugPricing(true);
    setIsLoadingPharmacies(true);
    setOnlinePharmacy(undefined);
    setMailPharmacy(undefined);
    setIsLoadingOnlinePharmacy(false);
    setIsLoadingMailPharmacy(false);
    setCurrentPage(pageNum);
  };

  const handleToggleChange = useCallback(() => {
    setIsNoDeductible(() => !isNoDeductible);
  }, [isNoDeductible]);

  const drugPricingToggleLabel = useMemo(() => {
    if (drugId) {
      const content = `${translate("PHARMACIES.pharmacies.drugPricinIsAnEstimate")}`;
      let identifier = "";
      if (i18n.language === "es") {
        if (fullGender === "Male") identifier += " " + translate("PHARMACIES.pharmacies.hombre");
        else if (fullGender === "Female") identifier += " " + translate("PHARMACIES.pharmacies.mujer");
        else identifier += " " + translate("PHARMACIES.pharmacies.person");
      }
      identifier += ` ${age} ${translate("PHARMACIES.pharmacies.yearOld")}`;

      if (i18n.language === "en") {
        if (fullGender === "Male") identifier += " " + translate("FIND_PHARMACIES.drugDetailsForm.male");
        else if (fullGender === "Female") identifier += " " + translate("FIND_PHARMACIES.drugDetailsForm.female");
        else identifier += " " + translate("PHARMACIES.pharmacies.person");
      }

      let dateInfo = "";
      if (claimRunDate) {
        dateInfo += ` ${new Date(claimRunDate).toLocaleDateString("en-US", {
          month: "long",
          day: "numeric",
          year: "numeric",
        })}`;
      }
      return <Body size="small">
        {content}
        <Semibold>{identifier}</Semibold>
        {claimRunDate && " " + translate("PHARMACIES.pharmacies.on")}
        <Semibold>{dateInfo}</Semibold>
        {". " + translate("PHARMACIES.pharmacies.theMemberResponsibilityMayVary")}
      </Body>;
    } else {
      return null;
    }
  }, [drugId, age, claimRunDate, i18n.language]);

  const onPrint = useCallback(() => {
    window.open(`${window.location.protocol}//${window.location.host}/print-pharmacies${window.location.search}`, "_blank").focus();
  }, []);

  useEffect(() => {
    hasDrugInformation ? logEvent(ANALYTIC_EVENTS.DRUG_PRICING_RESULTS_PAGE) : logEvent(ANALYTIC_EVENTS.PHARMACY_ONLY_SEARCH_RESULTS);
  }, [hasDrugInformation]);

  const pharmaciesToDisplay: CombinedPharmacyAndDrugInfo[] = useMemo(() => {
    if (!drugPricing?.pricing) {
      return concatenatedPharmaciesList;
    }
    setIsLoadingPharmacies(true);
    const pharmaciesToSort = [];
    const drugPricesToSort = drugPricing.pricing;

    if (sortBy === "low_to_high") {
      if (isNoDeductible) {
        drugPricesToSort.sort((a, b) => a.patient_pay_amount_no_deductible - b.patient_pay_amount_no_deductible);
      } else {
        drugPricesToSort.sort((a, b) => a.patient_pay_amount - b.patient_pay_amount);
      }
    }

    drugPricesToSort.forEach((drug) => {
      const associatedPharmacy = concatenatedPharmaciesList.find((pharmacy) => pharmacy.pharmacy.npi === drug.pharmacy_npi);
      pharmaciesToSort.push({
        ...associatedPharmacy,
        drug: {
          ...drug,
          dosage,
          dosage_form: dosageForm,
          days_supply: daysSupply,
          quantity: +quantity,
          drug_name: drugPricing?.drug?.drug_name,
        },
      });
    });

    setIsLoadingPharmacies(false);
    return pharmaciesToSort;
  }, [concatenatedPharmaciesList, drugPricing?.pricing, isNoDeductible]);

  // Fetch a single pharmacy and its drug pricing for use with online and/or mail pharmacy
  const fetchOnePharmacy = async (npi: string, pharmacyType: "mail" | "online", pharmacies?: CombinedPharmacyAndDrugInfo[]) => {
    let description = "";
    if (pharmacyType === "mail") {
      setIsLoadingMailPharmacy(true);
      description = translate("PHARMACIES.pharmacies.mailOrderDescription");
    } else {
      setIsLoadingOnlinePharmacy(true);
      description = translate("PHARMACIES.pharmacies.onlineDescription");
    }
    try {
      const pharmacy = await getPharmacyAPI({ npi: npi });
      const price = await priceOnePharmacy(npi);

      if (price.pricing.length == 1) {
        const pricedPharmacy = {
          pharmacy: {
            ...pharmacy,
            address_line_1: description,
          },
          drug: {
            ...price.drug,
            ...price.pricing[0],
          },
        };
        if (pharmacyType === "mail") {
          setMailPharmacy(pricedPharmacy);
        } else if (pharmacies?.length > 0) {
          // only show online pharmacy if it has the lowest price on the page
          if (isNoDeductible &&
              pricedPharmacy.drug?.patient_pay_amount_no_deductible !== undefined &&
              pricedPharmacy.drug?.patient_pay_amount_no_deductible !== null) {
            const lowerPriceThanOnline = pharmacies.find((pharmacy) =>
              pharmacy.drug?.patient_pay_amount_no_deductible !== undefined &&
            pharmacy.drug?.patient_pay_amount_no_deductible !== null &&
              pharmacy.drug.patient_pay_amount_no_deductible < pricedPharmacy.drug.patient_pay_amount_no_deductible);
            if (!lowerPriceThanOnline) {
              setOnlinePharmacy(pricedPharmacy);
            }
          } else if (pricedPharmacy.drug?.patient_pay_amount !== undefined && pricedPharmacy.drug?.patient_pay_amount !== null) {
            const lowerPriceThanOnline = pharmacies.find((pharmacy) =>
              pharmacy.drug?.patient_pay_amount !== undefined &&
              pharmacy.drug?.patient_pay_amount !== null &&
              pharmacy.drug.patient_pay_amount < pricedPharmacy.drug.patient_pay_amount);
            if (!lowerPriceThanOnline) {
              setOnlinePharmacy(pricedPharmacy);
            }
          }
        }
      } else {
        console.error("Unexpected drug pricing response");
        setMailPharmacy(null);
      }
      if (pharmacyType === "mail") {
        setIsLoadingMailPharmacy(false);
      } else {
        setIsLoadingOnlinePharmacy(false);
      }
    } catch (err) {
      console.error("Failed to fetch single pharmacy", err);
      if (axios.isAxiosError(err)) {
        setCanContinue(true);
      }
      if (pharmacyType === "mail") {
        setIsLoadingMailPharmacy(false);
        setMailPharmacy(undefined);
      } else {
        setIsLoadingOnlinePharmacy(false);
        setOnlinePharmacy(undefined);
      }
    }
  };

  useEffect(() => {
    const canLoadDrugs = !!drugId && !!dosage && !!dosageForm && !!quantity;
    if (!canLoadDrugs || currentPage != 0 || isLoadingPharmacies) return;

    if (!hideMailPharmacyOnFBPTool && mailPharmacyIdentifier && mailPharmacyIdentifier.length > 0) {
      fetchOnePharmacy(mailPharmacyIdentifier, "mail");
    }
  }, [
    currentPage,
    drugId,
    dosage,
    dosageForm,
    quantity,
    hideMailPharmacyOnFBPTool,
    isLoadingPharmacies,
    mailPharmacyIdentifier,
  ]);

  useEffect(() => {
    const canLoadDrugs = !!drugId && !!dosage && !!dosageForm && !!quantity;
    if (!canLoadDrugs || currentPage != 0) return;
    // only show online pharmacy if price is lowest; do not fetch until prices ready to compare
    if (isLoadingPharmacies || isLoadingDrugPricing || !pharmaciesToDisplay || !drugPricing?.pricing) return;

    if (!hideOnlinePharmacyOnFBPTool && onlinePharmacyIdentifier && onlinePharmacyIdentifier.length > 0) {
      fetchOnePharmacy(onlinePharmacyIdentifier, "online", pharmaciesToDisplay);
    }
  }, [
    currentPage,
    drugId,
    dosage,
    dosageForm,
    quantity,
    hideOnlinePharmacyOnFBPTool,
    isLoadingPharmacies,
    isLoadingDrugPricing,
    pharmaciesToDisplay,
    drugPricing,
    onlinePharmacyIdentifier,
  ]);


  const map = useMemo(
    () => (
      <AppleMap
        isLoading={isLoadingDrugPricing || isLoadingPharmacies}
        renderCalloutContent={(location) => (
          <PharmacyMapCallout distance={location.data?.distance} id={location.subtitle} data={location.data as PharmacyInfo} />
        )}
        center={center}
        locations={pharmacyData?.map(
          (item): MapLocation => ({
            latitude: parseFloat(item.pharmacy.latitude),
            longitude: parseFloat(item.pharmacy.longitude),
            title: item.pharmacy.name,
            data: item.pharmacy,
          }),
        )}
        onSearchArea={handleMapRedoSearch}
      />
    ),
    [pharmacyData, isLoadingDrugPricing, isLoadingPharmacies],
  );

  return (
    <PageLayout>
      <ConditionalErrorModal
        isVisible={modalVisible}
        onClose={closeModal}
        approveText={translate("PHARMACIES.pharmacies.continue")}
        denyText={translate("PHARMACIES.pharmacies.backToSearch")}
        title={modalTitle}
        canContinue={canContinue}
      >
        {isACAHIVPrEPRejection[0] && (
          <Form form={submissionClarificationCodeForm}>
            <Stack>
              <Paragraph>{translate("PHARMACIES.pharmacies.thisDrugRequiresAdditionalInformation")}</Paragraph>
              <Grid>
                <Grid.Span columns={6}>
                  <Dropdown.Field
                    required
                    label={translate("PHARMACIES.pharmacies.drugCategory")}
                    name="submission_clarification_code"
                    validator={(value) => !!value}
                  >
                    <Dropdown.Option value={"10"}>{translate("PHARMACIES.pharmacies.hivPrep")}</Dropdown.Option>
                    <Dropdown.Option value={"01"}>{translate("PHARMACIES.pharmacies.hivNonPrep")}</Dropdown.Option>
                  </Dropdown.Field>
                </Grid.Span>
              </Grid>
            </Stack>
          </Form>
        )}
        {!isACAHIVPrEPRejection[0] && modalText}
      </ConditionalErrorModal>
      <Stack spacing="medium">
        <Breadcrumbs planName={enrollmentPlanCsid?.display_name} />
        <Stack spacing="none">
          <ThemedP>
            <SearchDetailsSection
              drugDetails={hasDrugInformation ? pharmaciesToDisplay?.[0]?.drug : null}
              isLoading={isLoadingDrugPricing || isLoadingPharmacies}
              onBackButtonClicked={goBackToSearchPage}
              onPrintButtonClicked={showPharmaciesPrint ? onPrint : null}
            />
          </ThemedP>
        </Stack>
        <Card noPadding>
          <Grid style={{ padding: "1.5rem" }}>
            <Grid.Span columns={halfColumns}>
              <Stack spacing="medium-large">
                {drugId && (
                  <Stack spacing="small">
                    <Toggle
                      id="isNoDeductible"
                      isChecked={isNoDeductible}
                      name="isNoDeductible"
                      onChange={handleToggleChange}
                      toggleLabel={<Semibold>{translate("PHARMACIES.pharmacies.showPricingAfterDeductibleIsMet")}</Semibold>}
                    />
                    {drugPricingToggleLabel}
                  </Stack>
                )}
                <LoadingGroup loading={isLoadingMailPharmacy}>
                  <LoadingGroup.Placeholder
                    fallback={<LoadingSkeleton style={{ height: "95px", width: "100%", marginBottom: "1.5rem" }} count={1} />}
                  >
                    {mailPharmacy && (
                      <>
                        <Semibold>{translate("PHARMACIES.pharmacies.mailOrder")}</Semibold>
                        <PharmacyCard
                          key="mail"
                          pharmacyData={mailPharmacy}
                          isNoDeductible={isNoDeductible}
                          showGetPharmacyDirectionsButton={!hasDrugInformation && showPharmaciesPrint}
                        />
                      </>
                    )}
                  </LoadingGroup.Placeholder>
                </LoadingGroup>
                <LoadingGroup loading={isLoadingOnlinePharmacy}>
                  <LoadingGroup.Placeholder
                    fallback={<LoadingSkeleton style={{ height: "95px", width: "100%", marginBottom: "1.5rem" }} count={1} />}
                  >
                    {onlinePharmacy && (
                      <>
                        <Shelf>
                          <Semibold>{translate("PHARMACIES.pharmacies.online")}</Semibold>
                          <Tag role="note">{translate("PHARMACIES.pharmacies.lowestPrice")}</Tag>
                        </Shelf>
                        <PharmacyCard
                          key="mail"
                          pharmacyData={onlinePharmacy}
                          isNoDeductible={isNoDeductible}
                          showGetPharmacyDirectionsButton={!hasDrugInformation && showPharmaciesPrint}
                        />
                      </>
                    )}
                  </LoadingGroup.Placeholder>
                </LoadingGroup>
                {hasDrugInformation && (
                  <Shelf>
                    <TextDropdown prefix={<SortSvg />} value={sortBy} onChange={(value) => setSortBy(value as SortValues)} hideLabel>
                      {SORT_OPTIONS.map((option) => (
                        <Dropdown.Option value={option.value} key={option.value}>
                          {option.label}
                        </Dropdown.Option>
                      ))}
                    </TextDropdown>
                    <Shelf.Spacer />
                    {!isMobile && (
                      <Body size="small">
                        <Semibold>Estimated amount you'll pay:</Semibold>
                      </Body>
                    )}
                  </Shelf>
                )}
                <LoadingGroup loading={isLoadingDrugPricing || isLoadingPharmacies}>
                  <LoadingGroup.Placeholder
                    fallback={<LoadingSkeleton style={{ height: "95px", width: "100%", marginBottom: "1.5rem" }} count={6} />}
                  >
                    {pharmaciesToDisplay
                      && pharmaciesToDisplay.map((data, idx) => (
                        <PharmacyCard
                          key={idx}
                          pharmacyData={data}
                          isNoDeductible={isNoDeductible}
                          showGetPharmacyDirectionsButton={!hasDrugInformation && showPharmaciesPrint}
                        />
                      ))}
                  </LoadingGroup.Placeholder>
                </LoadingGroup>
                {isMobile && map}
                <Pagination selectedPage={currentPage} pageCount={pageCount} handleSelectPage={handleSelectPage} />
              </Stack>
            </Grid.Span>
            {!isMobile && <Grid.Span columns={6}>{map}</Grid.Span>}
          </Grid>
        </Card>
        <NeedHelp />
      </Stack>
    </PageLayout>
  );
};
